import React, { useMemo } from 'react';
import { ConfigProvider, theme } from 'antd';
import { resolveGround } from '../../vendor/skargard/engine/color';
import Visualization from '../../vendor/skargard/react/Visualization.jsx';
import { useTheme } from '../../context/ThemeContext';
import ValleySpirit from './ValleySpirit';

// The same piece with its own colours stripped: saturation 0 is the engine's
// switch for "paint with the theme's --art-paper and --art-ink" (see
// vendor/skargard/engine/color.js). Everything else — field, marks, motion —
// is untouched, and none of it is structural, so the running engine picks the
// change up live instead of reseeding.
function inThemeColours(piece) {
  return {
    ...piece,
    frame: { ...piece.frame, groundSaturation: 0 },
    layers: piece.layers.map(l => ({ ...l, ink: { ...l.ink, saturation: 0 } })),
  };
}

// A skargard piece as page furniture rather than as the subject.
//
// It owns the stacking so callers never have to think about z-index: the
// canvas is fixed at 0, the children sit in a relative layer at 1. Fixed, not
// absolute, so the piece stays put while a tall page scrolls over it.
//
// Pointer events are deliberately NOT disabled on the canvas. The piece binds
// the pointer to field.falloff, and because the content layer is above it, a
// card still receives every click it would have — the canvas only sees the
// pointer where nothing is covering it, which is exactly the margin we want it
// to react to.
//
// followTheme: the user's theme wins over the piece's. Where the piece's own
// ground would contradict it (a near-black piece in light mode), the piece is
// repainted in the theme's paper and ink instead, so the chrome can follow the
// user. Where they already agree the piece keeps its authored colours.
//
// showPiece={false} leaves the page bare (no canvas, no ground) while keeping
// the same element tree, so a page can bring the piece in and out without
// remounting its content.
export default function PieceBackdrop({ children, piece: Piece = ValleySpirit, dark, followTheme = false, showPiece = true }) {
  const { isDarkMode } = useTheme();
  const authored = Piece.piece;
  const authoredDark = authored.frame.groundLightness < 0.5;
  const clashes = followTheme
    && authored.frame.groundSaturation > 0
    && authoredDark !== isDarkMode;
  const piece = useMemo(() => (clashes ? inThemeColours(authored) : authored), [clashes, authored]);

  // The piece states its own ground (frame.groundSaturation > 0), so ask the
  // engine for it rather than restating the colour here — this is the same
  // call the canvas backend makes, so the pre-first-frame fill can never drift
  // out of step with what gets painted a frame later.
  const ground = resolveGround(piece.frame, 'var(--art-paper)');

  // A piece that paints a near-black ground makes light-mode chrome unreadable
  // on top of it, whatever the app theme is set to. So the content layer
  // follows the *piece*, not the user's theme: data-theme re-points the CSS
  // custom properties for this subtree (index.css scopes them to the bare
  // attribute, not to :root), and darkAlgorithm does the same for antd's own
  // tokens. Pass dark={false} to opt out for a light piece.
  //
  // At groundSaturation 0 the piece states no colour at all — it paints on
  // --art-paper, so it is already whatever the user's theme is, and pinning
  // the chrome would invert it. Only a piece with its own ground gets to
  // decide; the rest follow the app.
  const statesGround = piece.frame.groundSaturation > 0;
  const isDark = dark !== undefined
    ? dark
    : statesGround ? piece.frame.groundLightness < 0.5 : isDarkMode;

  const content = (
    <div data-theme={isDark ? 'dark' : 'light'} style={{ position: 'relative', zIndex: 1, minHeight: '100dvh' }}>
      {children}
    </div>
  );

  return (
    <div style={{ position: 'relative', minHeight: '100dvh', background: showPiece ? ground : undefined }}>
      <div aria-hidden style={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden' }}>
        {/* Always the same element under followTheme, so a theme toggle
            recolours the running piece rather than remounting its canvas. */}
        {showPiece && (followTheme ? <Visualization piece={piece} /> : <Piece />)}
      </div>
      <ConfigProvider theme={{ algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
        {content}
      </ConfigProvider>
    </div>
  );
}
